import { useMemo } from 'react';

import { CheckOutlined, DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

import type { ColorSchemePreference } from '@/config/colorScheme';
import { useColorScheme } from '@/contexts/ColorSchemeContext';

export type ColorSchemeDropdownOptions = {
  /** Gọi sau khi chọn một chế độ (ví dụ đóng menu cha). */
  onItemClick?: () => void;
};

/**
 * Menu Ant Design cho ba lựa chọn theme (hệ thống / sáng / tối), dùng chung cho UserBadge và màn đăng nhập.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook dùng chung; component ở cùng file
export function useColorSchemeDropdownItems(
  options?: ColorSchemeDropdownOptions,
): MenuProps['items'] {
  const { t } = useTranslation();
  const { preference, setPreference } = useColorScheme();
  const onItemClick = options?.onItemClick;

  return useMemo(
    (): MenuProps['items'] => [
      {
        key: 'theme-system',
        icon: <DesktopOutlined />,
        label: (
          <span className="flex min-w-[168px] items-center justify-between gap-2 text-xs">
            <span>{t('userMenu.themeSystem', 'Theo hệ thống')}</span>
            {preference === 'system' ? (
              <CheckOutlined className="text-cyan-600 dark:text-cyan-400" />
            ) : null}
          </span>
        ),
        onClick: () => {
          setPreference('system');
          onItemClick?.();
        },
      },
      {
        key: 'theme-light',
        icon: <SunOutlined />,
        label: (
          <span className="flex min-w-[168px] items-center justify-between gap-2 text-xs">
            <span>{t('userMenu.themeLight', 'Sáng')}</span>
            {preference === 'light' ? (
              <CheckOutlined className="text-cyan-600 dark:text-cyan-400" />
            ) : null}
          </span>
        ),
        onClick: () => {
          setPreference('light');
          onItemClick?.();
        },
      },
      {
        key: 'theme-dark',
        icon: <MoonOutlined />,
        label: (
          <span className="flex min-w-[168px] items-center justify-between gap-2 text-xs">
            <span>{t('userMenu.themeDark', 'Tối')}</span>
            {preference === 'dark' ? (
              <CheckOutlined className="text-cyan-600 dark:text-cyan-400" />
            ) : null}
          </span>
        ),
        onClick: () => {
          setPreference('dark');
          onItemClick?.();
        },
      },
    ],
    [onItemClick, preference, setPreference, t],
  );
}

function preferenceTriggerIcon(preference: ColorSchemePreference) {
  switch (preference) {
    case 'system':
      return <DesktopOutlined />;
    case 'light':
      return <SunOutlined />;
    case 'dark':
      return <MoonOutlined />;
  }
}

type ColorSchemeSwitcherProps = {
  className?: string;
};

/**
 * Nút mở menu chọn theme; đồng bộ với `ColorSchemeProvider` và lựa chọn trong menu người dùng.
 */
export function ColorSchemeSwitcher({ className }: ColorSchemeSwitcherProps) {
  const { t } = useTranslation();
  const { preference } = useColorScheme();
  const items = useColorSchemeDropdownItems();

  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
      <button
        type="button"
        title={t('userMenu.appearance', 'Giao diện')}
        className={className}
        aria-label={t('userMenu.appearance', 'Giao diện')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 32,
          padding: 0,
          borderRadius: 20,
          background: 'var(--bg-card)',
          border: '1px solid var(--glass-border)',
          color: 'var(--text-primary)',
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        {preferenceTriggerIcon(preference)}
      </button>
    </Dropdown>
  );
}
